import { useEffect, useState } from "react";
import { errLog } from "../utils/logger";
import { getErrorMessage } from "../utils/errors";
import { useLoader } from "./useLoader";
import { useAlert } from "./useAlert";
import { baseURL, project as projectEndpoint } from "../config/variables";
import { ProjectDataProps } from "../Components/common/types";

export const useFetchProjectById = (projectId: string) => {
  const [project, setProject] = useState<ProjectDataProps | null>(null);
  const [isFetched, setIsFetched] = useState(false);
  const { showLoader, setShowLoader, LoaderComponent } = useLoader();
  const { showAlert } = useAlert();

  useEffect(() => {
    if (!projectId) return;

    const abortController = new AbortController();
    const signal = abortController.signal;
    const url = `${baseURL}${projectEndpoint}/${projectId}`;

    const fetchProject = async () => {
      setIsFetched(false); // Reset
      setShowLoader(true);
      try {
        const response = await fetch(url, {
          signal,
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const result = await response.json();
        if (!signal.aborted) {
          setProject(result.data);
        }
      } catch (err: unknown) {
        if (!(err instanceof DOMException && err.name === "AbortError")) {
          const errorMsg = getErrorMessage(err);
          errLog("Error fetching project by ID:", errorMsg);
          showAlert(true, "Error", errorMsg, false);
        }
      } finally {
        if (!signal.aborted) {
          setIsFetched(true);
          setShowLoader(false);
        }
      }
    };

    fetchProject();

    return () => {
      abortController.abort();
    };
  }, [projectId, setShowLoader, showAlert]);

  return { showLoader, project, isFetched, LoaderComponent };
};
